import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import ScrollToTop from "@/components/ScrollToTop";
import FloatingScrollNav from "@/components/FloatingScrollNav";
import OnboardingTour from "@/components/OnboardingTour";
import CookieConsent from "@/components/CookieConsent";
import useSiteAnalytics from "@/hooks/useSiteAnalytics";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Products from "@/pages/Products";
import ProductDetail from "@/pages/ProductDetail";
import { HowItWorks, Industries, FAQsPage, Services } from "@/pages/StaticPages";
import BookConsultation from "@/pages/BookConsultation";
import Contact from "@/pages/Contact";
import Pricing from "@/pages/Pricing";
import { Privacy, Terms, Security, Refund } from "@/pages/Legal";
import { NotFound, ServerError, Cookies } from "@/pages/ErrorPages";
import { ForgotPassword, ResetPassword } from "@/pages/ForgotResetPassword";
import { Portfolio, CaseStudy } from "@/pages/Portfolio";
import { Login, Register } from "@/pages/Auth";

import PortalDashboard from "@/pages/portal/PortalDashboard";
import { PortalProjects, PortalProjectDetail } from "@/pages/portal/PortalProjects";
import PortalMessages from "@/pages/portal/PortalMessages";
import PortalInvoices from "@/pages/portal/PortalInvoices";
import PortalReorder from "@/pages/portal/PortalReorder";
import PortalArtwork from "@/pages/portal/PortalArtwork";
import PortalVault from "@/pages/portal/PortalVault";
import PortalSupport from "@/pages/portal/PortalSupport";
import PortalProfile from "@/pages/portal/PortalProfile";
import AIAssistant from "@/pages/portal/AIAssistant";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import { AdminLeads, AdminClients, AdminContacts } from "@/pages/admin/AdminLeads";
import { AdminProjects } from "@/pages/admin/AdminProjects";
import AdminInvoices from "@/pages/admin/AdminInvoices";
import AdminShowcase from "@/pages/admin/AdminShowcase";
import AdminTurnkeyBrands from "@/pages/admin/AdminTurnkeyBrands";
import AdminTurnkeyInquiries from "@/pages/admin/AdminTurnkeyInquiries";
import AdminTurnkeyAnalytics from "@/pages/admin/AdminTurnkeyAnalytics";
import AdminReorderRequests from "@/pages/admin/AdminReorderRequests";
import AdminArtworkCenter from "@/pages/admin/AdminArtworkCenter";
import AdminBrandVault from "@/pages/admin/AdminBrandVault";
import AdminSupportCenter from "@/pages/admin/AdminSupportCenter";
import AdminAI from "@/pages/admin/AdminAI";
import AdminCollections from "@/pages/admin/AdminCollections";
import AdminBottleSizes from "@/pages/admin/AdminBottleSizes";
import AdminSamples from "@/pages/admin/AdminSamples";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminComplianceBadges from "@/pages/admin/AdminComplianceBadges";
import AdminSampleKits from "@/pages/admin/AdminSampleKits";
import AdminTrustDocuments from "@/pages/admin/AdminTrustDocuments";
import AdminEmailLog from "@/pages/admin/AdminEmailLog";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import SampleRequest from "@/pages/SampleRequest";
import TrustCertifications from "@/pages/TrustCertifications";
import TurnkeyBrands from "@/pages/TurnkeyBrands";
import TurnkeyBrandDetail from "@/pages/TurnkeyBrandDetail";

// Small component so useSiteAnalytics() runs inside <BrowserRouter>
// (the hook uses useLocation for SPA page_view tracking).
const AnalyticsMount = () => { useSiteAnalytics(); return null; };

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AnalyticsMount />
          <ScrollToTop />
          <Toaster position="top-right" theme="light" richColors closeButton />
          <FloatingScrollNav />
          <Routes>
            {/* Public marketing */}
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/products" element={<Products />} />
            <Route path="/products/:slug" element={<ProductDetail />} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/portfolio/:slug" element={<CaseStudy />} />
            <Route path="/services" element={<Services />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/industries" element={<Industries />} />
            <Route path="/faqs" element={<FAQsPage />} />
            <Route path="/book-consultation" element={<BookConsultation />} />
            <Route path="/request-samples" element={<SampleRequest />} />
            <Route path="/trust" element={<TrustCertifications />} />
            <Route path="/turnkey" element={<TurnkeyBrands />} />
            <Route path="/turnkey/:slug" element={<TurnkeyBrandDetail />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/cookies" element={<Cookies />} />
            <Route path="/refund" element={<Refund />} />
            <Route path="/security" element={<Security />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/500" element={<ServerError />} />

            {/* Client Portal */}
            <Route path="/portal" element={<PortalDashboard />} />
            <Route path="/portal/projects" element={<PortalProjects />} />
            <Route path="/portal/projects/:id" element={<PortalProjectDetail />} />
            <Route path="/portal/messages" element={<PortalMessages />} />
            <Route path="/portal/invoices" element={<PortalInvoices />} />
            <Route path="/portal/reorder" element={<PortalReorder />} />
            <Route path="/portal/artwork" element={<PortalArtwork />} />
            <Route path="/portal/vault" element={<PortalVault />} />
            <Route path="/portal/support" element={<PortalSupport />} />
            <Route path="/portal/profile" element={<PortalProfile />} />
            <Route path="/portal/ai" element={<AIAssistant />} />

            {/* Admin */}
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/leads" element={<AdminLeads />} />
            <Route path="/admin/clients" element={<AdminClients />} />
            <Route path="/admin/projects" element={<AdminProjects />} />
            <Route path="/admin/invoices" element={<AdminInvoices />} />
            <Route path="/admin/showcase" element={<AdminShowcase />} />
            <Route path="/admin/turnkey-brands" element={<AdminTurnkeyBrands />} />
            <Route path="/admin/turnkey-inquiries" element={<AdminTurnkeyInquiries />} />
            <Route path="/admin/turnkey-analytics" element={<AdminTurnkeyAnalytics />} />
            <Route path="/admin/reorder-requests" element={<AdminReorderRequests />} />
            <Route path="/admin/artwork" element={<AdminArtworkCenter />} />
            <Route path="/admin/vault" element={<AdminBrandVault />} />
            <Route path="/admin/support" element={<AdminSupportCenter />} />
            <Route path="/admin/collections" element={<AdminCollections />} />
            <Route path="/admin/bottle-sizes" element={<AdminBottleSizes />} />
            <Route path="/admin/samples" element={<AdminSamples />} />
            <Route path="/admin/sample-kits" element={<AdminSampleKits />} />
            <Route path="/admin/compliance-badges" element={<AdminComplianceBadges />} />
            <Route path="/admin/trust-documents" element={<AdminTrustDocuments />} />
            <Route path="/admin/email-log" element={<AdminEmailLog />} />
            <Route path="/admin/analytics" element={<AdminAnalytics />} />
            <Route path="/admin/products" element={<AdminProducts />} />
            <Route path="/admin/contacts" element={<AdminContacts />} />
            <Route path="/admin/ai" element={<AdminAI />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          <OnboardingTour />
          <CookieConsent />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
